import { useCallback, useState } from 'react';

import cn from 'classnames';

import styles from './ScrollableArea.module.css';
import type { ScrollableAreaProps } from './types';

type MousePointerScrollableProps = Pick<
  ScrollableAreaProps,
  'isDraggable' | 'direction'
> & {
  ref: React.RefObject<HTMLDivElement>;
};

interface MousePointerScrollable {
  /**
   * The additional class name for the scrollable area
   */
  className?: string;
  /**
   * The function to handle the mouse down event
   */
  onMouseDown: (e: React.MouseEvent) => void;
}

const dragThreshold = 10;

/**
 * The hook to handle scrolling by dragging with a mouse
 */
export const useMousePointerScrollable = ({
  isDraggable,
  direction,
  ref,
}: MousePointerScrollableProps): MousePointerScrollable => {
  const [isDragging, setIsDragging] = useState(false);

  // if the area is dragged more than the threshold, this booealn will become true.
  // This is useful for components to decide whether this interaction was a "click on a child element",
  // or if it was a "drag/scroll of the parent component"
  const [hasReachedDragThreshold, setHasReachedDragThreshold] = useState(false);

  // Handling dragging with a mouse
  const handleMouseDown = useCallback(
    (e: React.MouseEvent): void => {
      if (!isDraggable || !ref.current) {
        return;
      }
      const element = ref.current;

      const horizontalDraggingEnabled =
        direction === 'both' || direction === 'horizontal';
      const verticalDraggingEnabled =
        direction === 'both' || direction === 'vertical';

      if (element) {
        setIsDragging(true);
        const mouseCoordsOnDragStart = { x: e.pageX, y: e.pageY };
        const scrollCoordsOnDragStart = {
          left: element.scrollLeft,
          top: element.scrollTop,
        };

        let draggableEventListenerCleanup = () => {};

        const handleMouseLeave = (): void => {
          setIsDragging(false);
          setHasReachedDragThreshold(false);
          draggableEventListenerCleanup();
        };

        const handleMouseUp = (): void => {
          setIsDragging(false);
          setHasReachedDragThreshold(false);
          draggableEventListenerCleanup();
        };

        const handleMouseMove = (e: MouseEvent): void => {
          e.preventDefault();

          if (horizontalDraggingEnabled) {
            const distanceFromStartCoord = e.pageX - mouseCoordsOnDragStart.x;
            element.scrollLeft =
              scrollCoordsOnDragStart.left - distanceFromStartCoord;
            if (Math.abs(distanceFromStartCoord) > dragThreshold) {
              setHasReachedDragThreshold(true);
            }
          }
          if (verticalDraggingEnabled) {
            const distanceFromStartCoord = e.pageY - mouseCoordsOnDragStart.y;
            element.scrollTop =
              scrollCoordsOnDragStart.top - distanceFromStartCoord;
            if (Math.abs(distanceFromStartCoord) > dragThreshold) {
              setHasReachedDragThreshold(true);
            }
          }
        };

        // adding the listeners on the document enables the drag behavior to still happen
        // and work correctly even if the mouse goes out of the bounds of the element
        document.addEventListener('mouseleave', handleMouseLeave);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        draggableEventListenerCleanup = () => {
          document.removeEventListener('mouseleave', handleMouseLeave);
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
    },
    [isDraggable, direction, ref]
  );

  return {
    className: cn({
      [styles['is-draggable']]: isDraggable,
      [styles['is-dragging']]: isDragging,
      [styles['prevent-child-clicks']]: hasReachedDragThreshold,
    }),
    onMouseDown: handleMouseDown,
  };
};
